import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Music4, Users, Send, Mail, Phone, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Reveal from "@/components/site/Reveal";
import { submitArtist, submitFanPartner } from "@/lib/api";

const initialArtist = { name: "", email: "", artist_name: "", genre: "", monthly_listeners: "", website: "", message: "" };
const initialFan = { name: "", email: "", interest_type: "fan", company: "", message: "" };

export default function Contact() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialTab = params.get("type") === "fan" || params.get("type") === "partner" ? "fan" : "artist";

  const [tab, setTab] = useState(initialTab);
  const [artist, setArtist] = useState(initialArtist);
  const [fan, setFan] = useState(initialFan);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState({ artist: false, fan: false });

  useEffect(() => { setTab(initialTab); }, [initialTab]);
  useEffect(() => { setDone({ artist: false, fan: false }); }, [tab]);

  const onArtist = async (e) => {
    e.preventDefault();
    setBusy(true);
    setDone((d) => ({ ...d, artist: false }));
    try {
      const res = await submitArtist(artist);
      toast.success("Thanks! We received your application.", {
        description: res.email_status === "mocked" ? "Notification pending (email service not yet configured)." : "We'll be in touch shortly."
      });
      setDone((d) => ({ ...d, artist: true }));
      setArtist(initialArtist);
    } catch (err) {
      toast.error("Something went wrong.", { description: err?.response?.data?.detail || "Please try again in a moment." });
    } finally {
      setBusy(false);
    }
  };

  const onFan = async (e) => {
    e.preventDefault();
    setBusy(true);
    setDone((d) => ({ ...d, fan: false }));
    try {
      const res = await submitFanPartner(fan);
      toast.success("Thanks! We received your message.", {
        description: res.email_status === "mocked" ? "Notification pending (email service not yet configured)." : "We'll get back to you soon."
      });
      setDone((d) => ({ ...d, fan: true }));
      setFan(initialFan);
    } catch (err) {
      toast.error("Something went wrong.", { description: err?.response?.data?.detail || "Please try again in a moment." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="page-contact" className="pt-24 md:pt-32">
      {/* HERO */}
      <section className="relative">
        <div className="container relative">
          <div className="am-blob" style={{ left: "50%", transform: "translateX(-50%)", top: "-30px" }} />
          <div className="text-center max-w-3xl mx-auto flex flex-col gap-6">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur px-4 py-1.5 text-[11px] uppercase tracking-[0.24em] text-primary font-semibold border border-black/5 self-center">
                Get Started
              </span>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="font-display font-bold tracking-tight text-[clamp(2.6rem,6.5vw,5.5rem)] leading-[0.98]">
                Say hi. <span className="font-serif-editorial italic font-normal">We'll open the door.</span>
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="text-lg md:text-xl text-foreground/65">
                Two forms below — one for artists ready to onboard, one for fans, press, and partners. Real replies, no autoresponders.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FORMS */}
      <section className="py-16 md:py-24">
        <div className="container grid lg:grid-cols-3 gap-8">
          {/* Sidebar */}
          <Reveal className="lg:col-span-1 order-2 lg:order-1">
            <div className="am-soft rounded-[32px] p-8 md:p-10 h-full flex flex-col gap-6 bg-white">
              <div className="text-[11px] uppercase tracking-[0.24em] text-primary font-semibold">Direct line</div>
              <div className="space-y-4 text-foreground/80">
                <div className="flex items-center gap-3"><Mail className="h-5 w-5 text-primary" /> keith@pulsbeat.ca</div>
                <div className="flex items-center gap-3"><Phone className="h-5 w-5 text-primary" /> +1 (403) 816-9715</div>
                <div className="flex items-start gap-3"><MapPin className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <div>Canada — Waterloo · Calgary</div>
                    <div>USA — Los Angeles</div>
                  </div>
                </div>
              </div>
              <div className="h-px bg-black/10" />
              <div>
                <div className="font-display font-bold text-lg">Why use the form?</div>
                <p className="text-foreground/60 text-sm mt-2 leading-relaxed">
                  Structured inquiries help us route you to the right person instantly — artist onboarding, partner deals, or press.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Forms */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <TabsList data-testid="contact-tabs" className="grid grid-cols-2 w-full h-14 bg-white shadow-sm border border-black/5 rounded-full p-1.5">
                <TabsTrigger
                  data-testid="tab-artist"
                  value="artist"
                  className="rounded-full h-full font-display font-semibold text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-[0_6px_20px_-4px_rgba(250,37,104,0.55)]"
                >
                  <Music4 className="h-4 w-4 mr-2" /> Artist
                </TabsTrigger>
                <TabsTrigger
                  data-testid="tab-fan"
                  value="fan"
                  className="rounded-full h-full font-display font-semibold text-sm data-[state=active]:bg-accent data-[state=active]:text-accent-foreground data-[state=active]:shadow-[0_6px_20px_-4px_rgba(255,122,26,0.55)]"
                >
                  <Users className="h-4 w-4 mr-2" /> Fan / Partner
                </TabsTrigger>
              </TabsList>

              {/* ARTIST FORM */}
              <TabsContent value="artist" className="mt-6">
                <form data-testid="artist-form" onSubmit={onArtist} className="am-soft rounded-[32px] p-6 md:p-10 flex flex-col gap-5 bg-white">
                  {done.artist && (
                    <div data-testid="artist-form-success" className="rounded-2xl bg-primary/10 text-primary flex items-center gap-3 px-4 py-3 border border-primary/20">
                      <CheckCircle2 className="h-5 w-5" /> Application received. A real human replies.
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Your name" htmlFor="a-name" required>
                      <Input id="a-name" data-testid="a-name" required value={artist.name} onChange={(e) => setArtist({ ...artist, name: e.target.value })} placeholder="Jane Doe" />
                    </Field>
                    <Field label="Email" htmlFor="a-email" required>
                      <Input id="a-email" data-testid="a-email" type="email" required value={artist.email} onChange={(e) => setArtist({ ...artist, email: e.target.value })} placeholder="jane@sound.com" />
                    </Field>
                    <Field label="Artist / project name" htmlFor="a-artist" required>
                      <Input id="a-artist" data-testid="a-artist-name" required value={artist.artist_name} onChange={(e) => setArtist({ ...artist, artist_name: e.target.value })} placeholder="Night Bloom" />
                    </Field>
                    <Field label="Primary genre" htmlFor="a-genre">
                      <Input id="a-genre" data-testid="a-genre" value={artist.genre} onChange={(e) => setArtist({ ...artist, genre: e.target.value })} placeholder="Alt R&B, House, ..." />
                    </Field>
                    <Field label="Monthly listeners" htmlFor="a-listeners">
                      <Select value={artist.monthly_listeners} onValueChange={(v) => setArtist({ ...artist, monthly_listeners: v })}>
                        <SelectTrigger id="a-listeners" data-testid="a-listeners"><SelectValue placeholder="Select a range" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="<1k">Under 1,000</SelectItem>
                          <SelectItem value="1k-10k">1,000 – 10,000</SelectItem>
                          <SelectItem value="10k-100k">10,000 – 100,000</SelectItem>
                          <SelectItem value="100k-1M">100,000 – 1,000,000</SelectItem>
                          <SelectItem value="1M+">1,000,000+</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Website / socials" htmlFor="a-website">
                      <Input id="a-website" data-testid="a-website" value={artist.website} onChange={(e) => setArtist({ ...artist, website: e.target.value })} placeholder="https://..." />
                    </Field>
                  </div>
                  <Field label="Tell us about your sound & goals" htmlFor="a-message" required>
                    <Textarea id="a-message" data-testid="a-message" required rows={5} value={artist.message} onChange={(e) => setArtist({ ...artist, message: e.target.value })} placeholder="What you're building, where you'd love AudioMine to plug in..." />
                  </Field>
                  <Button
                    data-testid="a-submit"
                    type="submit"
                    disabled={busy}
                    className="rounded-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold self-start px-8"
                  >
                    {busy ? "Sending…" : (<>Send application <Send className="ml-2 h-4 w-4" /></>)}
                  </Button>
                </form>
              </TabsContent>

              {/* FAN / PARTNER FORM */}
              <TabsContent value="fan" className="mt-6">
                <form data-testid="fan-form" onSubmit={onFan} className="am-soft rounded-[32px] p-6 md:p-10 flex flex-col gap-5 bg-white">
                  {done.fan && (
                    <div data-testid="fan-form-success" className="rounded-2xl bg-accent/10 text-accent flex items-center gap-3 px-4 py-3 border border-accent/20">
                      <CheckCircle2 className="h-5 w-5" /> Message received. Thanks for reaching out.
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Your name" htmlFor="f-name" required>
                      <Input id="f-name" data-testid="f-name" required value={fan.name} onChange={(e) => setFan({ ...fan, name: e.target.value })} placeholder="Alex Rivera" />
                    </Field>
                    <Field label="Email" htmlFor="f-email" required>
                      <Input id="f-email" data-testid="f-email" type="email" required value={fan.email} onChange={(e) => setFan({ ...fan, email: e.target.value })} placeholder="alex@email.com" />
                    </Field>
                    <Field label="I'm here as a…" htmlFor="f-type" required>
                      <Select value={fan.interest_type} onValueChange={(v) => setFan({ ...fan, interest_type: v })}>
                        <SelectTrigger id="f-type" data-testid="f-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fan">Music fan</SelectItem>
                          <SelectItem value="partner">Business / partner</SelectItem>
                          <SelectItem value="press">Press / media</SelectItem>
                          <SelectItem value="other">Something else</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Company / org (optional)" htmlFor="f-company">
                      <Input id="f-company" data-testid="f-company" value={fan.company} onChange={(e) => setFan({ ...fan, company: e.target.value })} placeholder="Acme Records" />
                    </Field>
                  </div>
                  <Field label="How can we help?" htmlFor="f-message" required>
                    <Textarea id="f-message" data-testid="f-message" required rows={5} value={fan.message} onChange={(e) => setFan({ ...fan, message: e.target.value })} placeholder="Tell us what you're excited about, or what you'd like to explore..." />
                  </Field>
                  <Button
                    data-testid="f-submit"
                    type="submit"
                    disabled={busy}
                    className="rounded-full h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold self-start px-8"
                  >
                    {busy ? "Sending…" : (<>Send message <ArrowRight className="ml-2 h-4 w-4" /></>)}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>
    </main>
  );
}

function Field({ label, htmlFor, required, children }) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={htmlFor} className="text-xs uppercase tracking-widest text-foreground/55 font-semibold">
        {label}{required && <span className="text-primary ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}
